import { useEffect, useRef, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "react-query";
import { Loader2, X } from "lucide-react";
import { fetchCurrencies, postGoal, updateGoal } from "../../utilities/api";
import { getInvestmentYearLabel } from "../home/shared";
import { Button } from "../Button";
import { cn } from "../../utilities/helpers";
import { formatWholeCurrency } from "./GoalCard";

const FOCUSABLE =
	'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

// No Dialog primitive exists in this codebase, so the accessibility contract is
// implemented once here and reused by every goals dialog: role/aria-modal, a
// focus trap, Escape to close, focus restored to whatever opened it, and the
// page behind locked from scrolling.
export const DialogShell = ({
	open,
	onClose,
	labelledBy,
	describedBy,
	className,
	children,
}) => {
	const containerRef = useRef(null);
	// onClose is typically an inline arrow, so a new identity every render would
	// re-run the effect and steal focus back on each keystroke.
	const onCloseRef = useRef(onClose);
	useEffect(() => {
		onCloseRef.current = onClose;
	});

	useEffect(() => {
		if (!open) return undefined;

		const previouslyFocused = document.activeElement;
		const node = containerRef.current;
		const target =
			node?.querySelector("[data-autofocus]") || node?.querySelector(FOCUSABLE);
		target?.focus();

		const handleKeyDown = (event) => {
			if (event.key === "Escape") {
				event.stopPropagation();
				onCloseRef.current?.();
				return;
			}
			if (event.key !== "Tab") return;

			const items = Array.from(node?.querySelectorAll(FOCUSABLE) || []).filter(
				(element) => element.offsetParent !== null
			);
			if (!items.length) return;

			const first = items[0];
			const last = items[items.length - 1];
			if (event.shiftKey && document.activeElement === first) {
				event.preventDefault();
				last.focus();
			} else if (!event.shiftKey && document.activeElement === last) {
				event.preventDefault();
				first.focus();
			}
		};

		document.addEventListener("keydown", handleKeyDown, true);
		const previousOverflow = document.body.style.overflow;
		document.body.style.overflow = "hidden";

		return () => {
			document.removeEventListener("keydown", handleKeyDown, true);
			document.body.style.overflow = previousOverflow;
			if (previouslyFocused instanceof HTMLElement && previouslyFocused.isConnected) {
				previouslyFocused.focus();
			}
		};
	}, [open]);

	if (!open) return null;

	return (
		<div
			className="fixed inset-0 z-50 flex items-end justify-center bg-slate-900/40 sm:items-center sm:p-4"
			onMouseDown={(event) => {
				if (event.target === event.currentTarget) onClose();
			}}
		>
			<div
				ref={containerRef}
				role="dialog"
				aria-modal="true"
				aria-labelledby={labelledBy}
				aria-describedby={describedBy}
				className={cn(
					"max-h-[92vh] w-full overflow-y-auto rounded-t-xl border border-slate-200 bg-white p-5 shadow-lg sm:max-w-lg sm:rounded-xl",
					className
				)}
			>
				{children}
			</div>
		</div>
	);
};

export const DialogHeader = ({ titleId, title, descriptionId, description, onClose }) => (
	<div className="mb-5 flex items-start justify-between gap-4">
		<div>
			<h2 id={titleId} className="text-lg font-semibold text-slate-900">
				{title}
			</h2>
			{description && (
				<p id={descriptionId} className="mt-1 text-sm text-slate-500">
					{description}
				</p>
			)}
		</div>
		<button
			type="button"
			onClick={onClose}
			aria-label="Close dialog"
			className="inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-md border border-slate-200 bg-white text-slate-500 transition-colors hover:bg-slate-100 hover:text-slate-900 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-slate-400"
		>
			<X className="h-4 w-4" aria-hidden="true" />
		</button>
	</div>
);

const controlClass =
	"h-9 w-full rounded-md border border-slate-200 bg-white px-3 text-sm text-slate-900 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-slate-400 disabled:bg-slate-50 disabled:text-slate-500";

const labelClass = "mb-1 block text-sm font-medium text-slate-700";

const FieldError = ({ id, message }) =>
	message ? (
		<p id={id} className="mt-1 text-xs text-rose-700">
			{message}
		</p>
	) : null;

const buildYearOptions = (type, selectedYear) => {
	const currentYear = getInvestmentYearLabel(new Date());
	const start = type === "FINAL" ? currentYear : currentYear - 1;
	const end = type === "FINAL" ? currentYear + 40 : currentYear + 15;
	const years = [];
	for (let year = start; year <= end; year += 1) years.push(year);
	// An existing goal can sit outside the range (an old year, or a final goal
	// further out than the window); it still has to be selectable while editing.
	const selected = Number(selectedYear);
	if (Number.isFinite(selected) && !years.includes(selected)) {
		years.push(selected);
		years.sort((left, right) => left - right);
	}
	return years;
};

const PINNED_CURRENCIES = ["USD", "CHF", "EUR", "GBP", "INR"];

// Default to the first investment year that doesn't have a goal yet: opening
// the form pre-loaded with a duplicate year would greet the user with an error
// before they have typed anything.
const emptyForm = (goals = []) => {
	const currentYear = getInvestmentYearLabel(new Date());
	const taken = new Set(
		goals
			.filter((item) => item.type === "YEARLY")
			.map((item) => Number(item.year))
	);
	let year = currentYear;
	while (taken.has(year) && year < currentYear + 15) year += 1;
	return { type: "YEARLY", year: String(year), amount: "", currency: "USD", note: "" };
};

const GoalFormModal = ({ open, goal, goals = [], onClose }) => {
	const queryClient = useQueryClient();
	const [form, setForm] = useState(emptyForm);
	const [fieldErrors, setFieldErrors] = useState({});
	const [formError, setFormError] = useState("");
	const isEditing = Boolean(goal?.id);

	const { data: currencyRates } = useQuery("fetchCurrencies", fetchCurrencies);

	useEffect(() => {
		if (!open) return;
		setFormError("");
		setFieldErrors({});
		if (goal?.id) {
			setForm({
				type: goal.type,
				year: String(goal.year),
				// sourceAmount/sourceCurrency, not the USD-normalized `amount` --
				// editing a CHF goal has to show what was actually entered.
				amount: String(goal.sourceAmount ?? goal.amount ?? ""),
				currency: goal.sourceCurrency || goal.currency || "USD",
				note: goal.note || "",
			});
		} else {
			// Reads the goal list as it was when the dialog opened, on purpose:
			// depending on the array prop would reset the form on every render.
			setForm(emptyForm(goals));
		}
	}, [open, goal]);

	const onSuccess = () => {
		queryClient.invalidateQueries("fetchGoals");
		onClose();
	};
	const onError = (err) =>
		setFormError(
			err?.response?.data?.error ||
				(err?.message === "Network Error"
					? "Couldn't reach the server. Check your connection and try again."
					: err?.message) ||
				"Something went wrong"
		);

	const createMutation = useMutation(postGoal, { onSuccess, onError });
	const updateMutation = useMutation(updateGoal, { onSuccess, onError });
	const isSaving = createMutation.isLoading || updateMutation.isLoading;

	const handleChange = (event) => {
		const { name, value } = event.target;
		setForm((prev) => ({ ...prev, [name]: value }));
		setFieldErrors((prev) => ({ ...prev, [name]: "" }));
	};

	const finalGoalExists = goals.some(
		(item) => item.type === "FINAL" && item.id !== goal?.id
	);
	const yearTaken =
		form.type === "YEARLY" &&
		goals.some(
			(item) =>
				item.type === "YEARLY" &&
				item.id !== goal?.id &&
				String(item.year) === String(form.year)
		);

	const currencyOptions = (() => {
		const names = Object.keys(currencyRates || {}).sort();
		const pinned = PINNED_CURRENCIES.filter((name) => names.includes(name));
		const rest = names.filter((name) => !PINNED_CURRENCIES.includes(name));
		const known = new Set(names);
		const selected = (form.currency || "USD").toUpperCase();
		return {
			pinned,
			rest,
			// Keeps an unlisted code (or the default before rates load) selectable.
			extra: known.has(selected) ? null : selected,
		};
	})();

	// Digit grouping and stray spaces are what people actually paste in.
	const parsedAmount = parseFloat(String(form.amount).replace(/[\s,]/g, ""));
	const amountPreview =
		Number.isFinite(parsedAmount) && parsedAmount > 0
			? formatWholeCurrency(parsedAmount, { name: (form.currency || "USD").toUpperCase() })
			: null;

	const handleSubmit = (event) => {
		event.preventDefault();
		setFormError("");

		const nextErrors = {};
		if (!form.year) nextErrors.year = "Choose a target year.";
		if (yearTaken) {
			nextErrors.year = `You already have a ${form.year} goal. Edit that one instead.`;
		}
		if (!Number.isFinite(parsedAmount) || parsedAmount <= 0) {
			nextErrors.amount = "Enter a target amount greater than 0.";
		}
		if (!/^[A-Za-z]{3}$/.test(form.currency)) {
			nextErrors.currency = "Use a 3-letter code such as USD.";
		}

		setFieldErrors(nextErrors);
		if (Object.keys(nextErrors).length > 0) {
			const firstField = ["year", "amount", "currency"].find((name) => nextErrors[name]);
			document.getElementById(`goal-${firstField}`)?.focus();
			return;
		}

		const payload = {
			year: form.year,
			amount: parsedAmount,
			currency: form.currency.toUpperCase(),
			note: form.note.trim(),
		};

		if (isEditing) {
			updateMutation.mutate({ id: goal.id, ...payload });
		} else {
			createMutation.mutate({ type: form.type, ...payload });
		}
	};

	return (
		<DialogShell
			open={open}
			onClose={onClose}
			labelledBy="goal-dialog-title"
			describedBy="goal-dialog-description"
		>
			<DialogHeader
				titleId="goal-dialog-title"
				title={isEditing ? "Edit goal" : "New goal"}
				descriptionId="goal-dialog-description"
				description="A target is the portfolio value you want to reach by the end of an investment year (11 December)."
				onClose={onClose}
			/>

			<form onSubmit={handleSubmit} className="space-y-4" noValidate>
				{isEditing ? (
					<div>
						<span className={labelClass}>Goal type</span>
						<p className="text-sm text-slate-900">
							{form.type === "FINAL" ? "Final goal" : "Yearly target"}
						</p>
						<p className="mt-1 text-xs text-slate-500">
							Type can't be changed. Delete the goal and create a new one instead.
						</p>
					</div>
				) : (
					<fieldset>
						<legend className={labelClass}>Goal type</legend>
						<div className="grid gap-2 sm:grid-cols-2">
							{[
								{
									value: "YEARLY",
									title: "Yearly target",
									hint: "Where you want to land by one 11 December.",
									disabled: false,
								},
								{
									value: "FINAL",
									title: "Final goal",
									hint: finalGoalExists
										? "You already have one — edit it instead."
										: "Your single long-range target.",
									disabled: finalGoalExists,
								},
							].map((option) => (
								<label
									key={option.value}
									className={cn(
										"flex cursor-pointer gap-2 rounded-md border p-3 transition-colors",
										form.type === option.value
											? "border-slate-900 bg-slate-50"
											: "border-slate-200 hover:bg-slate-50",
										option.disabled && "cursor-not-allowed opacity-60 hover:bg-white"
									)}
								>
									<input
										type="radio"
										name="type"
										value={option.value}
										checked={form.type === option.value}
										disabled={option.disabled}
										onChange={handleChange}
										className="mt-0.5 h-4 w-4 accent-slate-900 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-slate-400"
									/>
									<span className="min-w-0">
										<span className="block text-sm font-medium text-slate-900">
											{option.title}
										</span>
										<span className="mt-0.5 block text-xs text-slate-500">
											{option.hint}
										</span>
									</span>
								</label>
							))}
						</div>
					</fieldset>
				)}

				<div>
					<label htmlFor="goal-year" className={labelClass}>
						Target year
					</label>
					<select
						id="goal-year"
						name="year"
						value={form.year}
						onChange={handleChange}
						className={controlClass}
						aria-invalid={Boolean(fieldErrors.year || yearTaken)}
						aria-describedby={
							fieldErrors.year || yearTaken ? "goal-year-error" : "goal-year-hint"
						}
					>
						{buildYearOptions(form.type, form.year).map((year) => (
							<option key={year} value={year}>
								{year}
							</option>
						))}
					</select>
					{fieldErrors.year || yearTaken ? (
						<FieldError
							id="goal-year-error"
							message={
								fieldErrors.year ||
								`You already have a ${form.year} goal. Edit that one instead.`
							}
						/>
					) : (
						<p id="goal-year-hint" className="mt-1 text-xs text-slate-500">
							Due 11 Dec {form.year}.
						</p>
					)}
				</div>

				<div className="grid grid-cols-[1fr_7rem] gap-3">
					<div>
						<label htmlFor="goal-amount" className={labelClass}>
							Target amount
						</label>
						<input
							id="goal-amount"
							name="amount"
							type="text"
							inputMode="decimal"
							autoComplete="off"
							data-autofocus
							value={form.amount}
							onChange={handleChange}
							placeholder="185000"
							className={controlClass}
							aria-invalid={Boolean(fieldErrors.amount)}
							aria-describedby={
								fieldErrors.amount ? "goal-amount-error" : "goal-amount-hint"
							}
						/>
						{fieldErrors.amount ? (
							<FieldError id="goal-amount-error" message={fieldErrors.amount} />
						) : (
							<p id="goal-amount-hint" className="mt-1 text-xs text-slate-500">
								{amountPreview ? `${amountPreview} portfolio value.` : "Portfolio value to reach."}
							</p>
						)}
					</div>
					<div>
						<label htmlFor="goal-currency" className={labelClass}>
							Currency
						</label>
						<select
							id="goal-currency"
							name="currency"
							value={(form.currency || "USD").toUpperCase()}
							onChange={handleChange}
							className={controlClass}
							aria-invalid={Boolean(fieldErrors.currency)}
							aria-describedby={fieldErrors.currency ? "goal-currency-error" : undefined}
						>
							{currencyOptions.extra && (
								<option value={currencyOptions.extra}>{currencyOptions.extra}</option>
							)}
							{currencyOptions.pinned.length > 0 && (
								<optgroup label="Common">
									{currencyOptions.pinned.map((name) => (
										<option key={name} value={name}>
											{name}
										</option>
									))}
								</optgroup>
							)}
							{currencyOptions.rest.length > 0 && (
								<optgroup label="All currencies">
									{currencyOptions.rest.map((name) => (
										<option key={name} value={name}>
											{name}
										</option>
									))}
								</optgroup>
							)}
						</select>
						<FieldError id="goal-currency-error" message={fieldErrors.currency} />
					</div>
				</div>

				<div>
					<label htmlFor="goal-note" className={labelClass}>
						Note <span className="font-normal text-slate-400">(optional)</span>
					</label>
					<input
						id="goal-note"
						name="note"
						type="text"
						value={form.note}
						onChange={handleChange}
						placeholder="e.g. Retirement"
						className={controlClass}
						aria-describedby="goal-note-hint"
					/>
					<p id="goal-note-hint" className="mt-1 text-xs text-slate-500">
						Shown on the goal card.
					</p>
				</div>

				{formError && (
					<div
						role="alert"
						className="rounded-md border border-rose-200 bg-rose-50 px-3 py-2 text-sm text-rose-700"
					>
						{formError}
					</div>
				)}

				<div className="flex justify-end gap-2 pt-1">
					<Button type="button" variant="outline" onClick={onClose} className="h-9">
						Cancel
					</Button>
					<Button type="submit" className="h-9" disabled={isSaving}>
						{isSaving && <Loader2 className="mr-2 h-4 w-4 animate-spin" aria-hidden="true" />}
						{isSaving ? "Saving..." : isEditing ? "Save changes" : "Create goal"}
					</Button>
				</div>
			</form>
		</DialogShell>
	);
};

export default GoalFormModal;
